import { apiRequest } from "@/lib/api";

export const foodCapabilityDefaults = {
  food_online_orders: true, food_delivery: true, food_pos: true, food_cash: true,
  food_management: true, food_kds: false, food_tables: false,
};
export type FoodCapability = keyof typeof foodCapabilityDefaults;
export type FoodCapabilities = Record<FoodCapability, boolean>;
export type FoodCapabilityStates = Record<FoodCapability, { allowed: boolean; enabled: boolean; effective: boolean }>;
type FoodCompany = { business_type?: string; food_capabilities?: Partial<FoodCapabilities> & { food_production?: boolean }; food_capability_states?: FoodCapabilityStates };

export const foodCapabilityLabels = {
  food_online_orders: "Pedidos online", food_delivery: "Delivery", food_pos: "PDV",
  food_cash: "Caixa", food_management: "Gestão interna", food_kds: "Cozinha / KDS", food_tables: "Mesas e comandas",
} as const;
export const foodPlanFields = {
  food_online_orders: "allow_food_online_orders", food_delivery: "allow_food_delivery",
  food_pos: "allow_food_pos", food_cash: "allow_food_cash", food_management: "allow_food_management",
} as const;
export const foodOptionalPlanFields = { food_kds: "allow_food_kds", food_tables: "allow_food_tables" } as const;
export const foodAllPlanFields = { ...foodPlanFields, ...foodOptionalPlanFields };
export type FoodPlanFeatures = Partial<Record<typeof foodAllPlanFields[keyof typeof foodAllPlanFields], boolean>>;
export function foodPlanFlags(plan: FoodPlanFeatures): FoodCapabilities {
  return { ...foodCapabilityDefaults, ...Object.fromEntries(Object.entries(foodAllPlanFields).map(([key, field]) => [key, plan[field as keyof FoodPlanFeatures] ?? foodCapabilityDefaults[key as FoodCapability]])) };
}
export function changeFoodPlanFeature<T extends FoodPlanFeatures>(plan: T, key: keyof typeof foodAllPlanFields, enabled: boolean): T {
  const flags = changeFoodCapability(foodPlanFlags(plan), key, enabled);
  return { ...plan, ...Object.fromEntries(Object.entries(foodAllPlanFields).map(([capability, field]) => [field, flags[capability as FoodCapability]])) };
}
export const foodCapabilityAllowed = (states: FoodCapabilityStates | undefined, key: FoodCapability): boolean => states?.[key]?.allowed ?? foodCapabilityDefaults[key];

export function foodCapabilityEnabled(company: FoodCompany | null | undefined, key: FoodCapability): boolean {
  if (company?.business_type !== "food") return false;
  const enabled = company.food_capability_states?.[key]?.effective ?? company.food_capabilities?.[key] ?? foodCapabilityDefaults[key];
  if (key === "food_tables") return enabled && foodCapabilityEnabled(company, "food_pos");
  if (key === "food_delivery") return enabled && foodCapabilityEnabled(company, "food_online_orders");
  if (key === "food_pos") return enabled && foodCapabilityEnabled(company, "food_cash");
  return enabled;
}

export function foodRouteCapability(pathname: string): FoodCapability | null {
  const routes: Array<[string, FoodCapability]> = [
    ["pdv", "food_pos"], ["caixa", "food_cash"], ["cozinha", "food_kds"], ["salao", "food_tables"],
    ["inventario", "food_management"], ["relatorios", "food_management"],
    ...["insumos", "fichas-tecnicas", "estoque", "fornecedores", "compras", "producao"].map(path => [path, "food_management"] as [string, FoodCapability]),
  ];
  return routes.find(([path]) => pathname === `/empresa/food/${path}` || pathname.startsWith(`/empresa/food/${path}/`))?.[1] ?? null;
}

export function foodRouteEnabled(company: FoodCompany, pathname: string): boolean {
  if (company.business_type === "food" && pathname.startsWith("/empresa/food/producao")) return company.food_capabilities?.food_production ?? foodCapabilityEnabled(company, "food_management");
  const capability = foodRouteCapability(pathname);
  return company.business_type !== "food" || capability === null || foodCapabilityEnabled(company, capability);
}

export function changeFoodCapability(current: FoodCapabilities, key: FoodCapability, enabled: boolean): FoodCapabilities {
  const next = { ...current, [key]: enabled };
  if (key === "food_online_orders" && !enabled) next.food_delivery = false;
  if (key === "food_delivery" && enabled) next.food_online_orders = true;
  if (key === "food_cash" && !enabled) next.food_pos = false;
  if (key === "food_pos" && enabled) next.food_cash = true;
  if (key === "food_tables" && enabled) { next.food_pos = true; next.food_cash = true; }
  if ((key === "food_pos" || key === "food_cash") && !enabled) next.food_tables = false;
  return next;
}

export type FoodCapabilitySettings = { food_capabilities: FoodCapabilities; food_operational_capabilities?: FoodCapabilities; food_capability_states?: FoodCapabilityStates; available_capabilities: FoodCapability[]; can_manage: boolean };
export const getFoodCapabilitySettings = () => apiRequest<FoodCapabilitySettings>("/companies/me/food-capabilities/", { authenticated: true });
export const saveFoodCapabilitySettings = (capabilities: FoodCapabilities, states?: FoodCapabilityStates) => apiRequest<FoodCapabilitySettings>("/companies/me/food-capabilities/", {
  authenticated: true, method: "PATCH",
  // Campos indisponíveis permanecem guardados; salvar outro recurso não os apaga.
  body: JSON.stringify(Object.fromEntries(Object.entries(capabilities).filter(([key, enabled]) => !states || states[key as FoodCapability]?.allowed || !enabled))),
});
